/**
 * Centralized environment variable configuration with Zod validation
 * 
 * All environment variables must be prefixed with VITE_ to be accessible in client code.
 * Server-only secrets (STRIPE_SECRET_KEY, SUPABASE_SERVICE_ROLE_KEY) are NOT included here.
 * 
 * Server-only secrets are configured in:
 * - Supabase Edge Functions: STRIPE_SECRET_KEY (via Supabase dashboard)
 * - Never exposed to client code
 */

import { z } from 'zod';

// Zod schema for environment validation
// Standardized on VITE_SUPABASE_ANON_KEY (preferred), supports VITE_SUPABASE_PUBLISHABLE_KEY for backward compatibility
const envSchema = z.object({
  VITE_SUPABASE_URL: z.string().url('VITE_SUPABASE_URL must be a valid URL').min(1),
  VITE_SUPABASE_PUBLISHABLE_KEY: z.string().min(1, 'VITE_SUPABASE_ANON_KEY (or VITE_SUPABASE_PUBLISHABLE_KEY) is required'),
  VITE_SITE_URL: z.string().url().optional().or(z.literal('')),
});

interface EnvConfig {
  supabaseUrl: string;
  supabasePublishableKey: string;
  siteUrl: string;
}

let envConfig: EnvConfig | null = null;
let validationError: string | null = null;

/**
 * Validates and returns environment configuration using Zod
 * Throws an error if required variables are missing or invalid
 */
function validateEnv(): EnvConfig {
  // Standardize on VITE_SUPABASE_ANON_KEY (preferred), fallback to VITE_SUPABASE_PUBLISHABLE_KEY for backward compatibility
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  
  const rawEnv = {
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
    VITE_SUPABASE_PUBLISHABLE_KEY: anonKey, // Internal name, accepts both variable names
    VITE_SITE_URL: import.meta.env.VITE_SITE_URL,
  };

  // Validate with Zod
  const result = envSchema.safeParse(rawEnv);

  if (!result.success) {
    const missing = result.error.errors
      .filter(e => e.code === 'too_small' || e.code === 'invalid_type')
      .map(e => e.path.join('.'));
    
    const invalid = result.error.errors
      .filter(e => e.code === 'invalid_string')
      .map(e => `${e.path.join('.')}: ${e.message}`);

    const errors: string[] = [];
    if (missing.length > 0) {
      // Provide helpful message if anon key is missing
      const missingVars = missing.map(v => {
        if (v === 'VITE_SUPABASE_PUBLISHABLE_KEY') {
          return 'VITE_SUPABASE_ANON_KEY (or VITE_SUPABASE_PUBLISHABLE_KEY for backward compatibility)';
        }
        return v;
      });
      errors.push(`Missing: ${missingVars.join(', ')}`);
    }
    if (invalid.length > 0) {
      errors.push(`Invalid: ${invalid.join('; ')}`);
    }

    const errorMsg = `Environment validation failed. ${errors.join('. ')}. Please configure these in your Vercel project settings (Settings → Environment Variables).`;
    console.error('[Env Config] Validation failed:', result.error.errors);
    throw new Error(errorMsg);
  }

  const validated = result.data;
  const siteUrl = validated.VITE_SITE_URL?.trim()
    || (import.meta.env.VITE_APP_URL as string | undefined)?.trim()
    || (typeof window !== 'undefined' ? window.location.origin : '');

  return {
    supabaseUrl: validated.VITE_SUPABASE_URL.trim(),
    supabasePublishableKey: validated.VITE_SUPABASE_PUBLISHABLE_KEY.trim(),
    siteUrl: siteUrl.trim() || (typeof window !== 'undefined' ? window.location.origin : ''),
  };
}

/**
 * Get environment configuration (cached after first call)
 */
export function getEnv(): EnvConfig {
  if (envConfig) {
    return envConfig;
  }

  try {
    envConfig = validateEnv();
    validationError = null;
    return envConfig;
  } catch (error) {
    validationError = error instanceof Error ? error.message : String(error);
    throw error;
  }
}

/**
 * Get validation error message (if any)
 */
export function getEnvError(): string | null {
  return validationError;
}

/**
 * Check if environment is valid (non-throwing)
 */
export function isEnvValid(): boolean {
  try {
    getEnv();
    return true;
  } catch {
    return false;
  }
}

/**
 * Get diagnostic info (for debugging, does not expose secrets)
 * DEV ONLY: Logs diagnostic info to console
 */
export function getEnvDiagnostics(): {
  hasSupabaseUrl: boolean;
  hasSupabaseKey: boolean;
  hasSiteUrl: boolean;
  hasStripePriceId: boolean;
  siteUrl: string;
  supabaseUrlHost: string;
  error: string | null;
  anonKeyPrefix: string; // First 6 chars only, for verification
} {
  const hasSupabaseUrl = !!(import.meta.env.VITE_SUPABASE_URL?.trim());
  // Standardize on VITE_SUPABASE_ANON_KEY (preferred), fallback to VITE_SUPABASE_PUBLISHABLE_KEY for compatibility
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim() || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();
  const hasSupabaseKey = !!anonKey;
  const hasSiteUrl = !!(import.meta.env.VITE_SITE_URL?.trim());
  const hasStripePriceId = !!(import.meta.env.VITE_STRIPE_PRICE_ID?.trim());
  const siteUrl = import.meta.env.VITE_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : 'N/A');
  
  // Extract host from Supabase URL for debugging (dev only, no secrets)
  let supabaseUrlHost = 'N/A';
  try {
    if (hasSupabaseUrl) {
      const url = new URL(import.meta.env.VITE_SUPABASE_URL);
      supabaseUrlHost = url.host;
    }
  } catch {
    supabaseUrlHost = 'Invalid URL';
  }

  // Extract first 6 chars of anon key for verification (DEV ONLY, never log full key)
  const anonKeyPrefix = anonKey ? anonKey.substring(0, 6) : 'missing';

  // DEV ONLY: Log diagnostic info
  if (import.meta.env.DEV) {
    console.log('[Env Diagnostics]', {
      VITE_SUPABASE_URL: hasSupabaseUrl,
      VITE_SUPABASE_ANON_KEY: hasSupabaseKey,
      anonKeyPrefix: anonKeyPrefix,
      supabaseUrlHost: supabaseUrlHost,
    });
  }

  return {
    hasSupabaseUrl,
    hasSupabaseKey,
    hasSiteUrl,
    hasStripePriceId,
    siteUrl,
    supabaseUrlHost,
    error: validationError,
    anonKeyPrefix,
  };
}

